import { useState, useLayoutEffect, type RefObject } from 'react';

// Engine geometry (dz-publishing-process createChart.js): 6px outward tick
// divisions + 8px gap between tick mark and label.
const AXIS_TICK_SIZE = 6;
const Y_TICK_LABEL_PADDING = 8;
const DEFAULT_WIDTH = 60;

/**
 * Flush-left y-axis per the dz-publishing-process chart engine: the left
 * margin is exactly `tickSize + tickPadding + widest tick label`, so the
 * widest y label's LEFT edge lands at the title/content edge (x = 0) —
 * "the plot aligns flush-left with the title/legend (DZ-Infografiken)".
 *
 * recharts 2.x has no auto-width YAxis, so this replicates the engine's
 * measure-then-reserve step: after each commit, measure the rendered y tick
 * texts and feed the result back as the YAxis `width` (chart margin.left
 * must be 0). Width changes don't alter y tick values, so the feedback
 * settles after one extra render.
 *
 * Returns the axis width — which is also the SVG x of the axis line, i.e.
 * the plot's left edge. The engine anchors the unit caption and the bottom
 * legend at that same plot edge, so reuse the value for both.
 */
export function useFlushLeftYAxisWidth(
  containerRef: RefObject<HTMLElement | null>,
  deps: unknown[],
): number {
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useLayoutEffect(() => {
    let raf = 0;
    const measure = (): boolean => {
      const ticks = containerRef.current?.querySelectorAll<SVGGraphicsElement>(
        '.recharts-yAxis .recharts-cartesian-axis-tick text',
      );
      if (!ticks || ticks.length === 0) return false;
      let max = 0;
      ticks.forEach(t => {
        const w = t.getBBox().width;
        if (w > max) max = w;
      });
      const next = Math.ceil(max) + AXIS_TICK_SIZE + Y_TICK_LABEL_PADDING;
      setWidth(prev => (Math.abs(prev - next) > 0.5 ? next : prev));
      return true;
    };
    // ResponsiveContainer draws the chart only after it has measured its
    // container, so the ticks may not exist on the first commit — retry on
    // the next animation frames until they do.
    if (!measure()) {
      let tries = 0;
      const retry = () => {
        if (!measure() && tries++ < 10) raf = requestAnimationFrame(retry);
      };
      raf = requestAnimationFrame(retry);
    }
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return width;
}
